import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle } from 'lucide-react';

export type ToastKind = 'success' | 'info' | 'warn' | 'error';

interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
}

interface ToastContextType {
  push: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 4000);
  }, []);

  const value = useMemo<ToastContextType>(
    () => ({
      push,
      success: (title, description) => push({ kind: 'success', title, description }),
      info: (title, description) => push({ kind: 'info', title, description }),
      error: (title, description) => push({ kind: 'error', title, description }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-96 flex-col gap-2">
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast }: { toast: Toast }): JSX.Element {
  const { kind, title, description } = toast;
  const Icon =
    kind === 'success'
      ? CheckCircle2
      : kind === 'error'
      ? XCircle
      : kind === 'warn'
      ? AlertTriangle
      : Info;
  const bgClass =
    kind === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
      : kind === 'error'
      ? 'border-red-200 bg-red-50 text-red-950'
      : kind === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : 'border-sky-200 bg-sky-50 text-sky-950';

  return (
    <div
      className={`pointer-events-auto flex gap-3 rounded-lg border px-4 py-3 shadow-lg ${bgClass}`}
    >
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-semibold">{title}</div>
        {description && <div className="mt-0.5 text-xs opacity-80">{description}</div>}
      </div>
    </div>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast fora de ToastProvider');
  return ctx;
}
